import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SiBoosty } from 'react-icons/si';

interface Product {
    id: string;
    title: string;
    description: string;
    images: string[];
    buy_link: string;
}

const ProductDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [product, setProduct] = useState<Product | null>(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isManualChange, setIsManualChange] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/products/${id}`)
            .then((response) => response.json())
            .then((data) => setProduct(data))
            .catch((error) => console.error('Error fetching product:', error));
    }, [id]);

    useEffect(() => {
        if (product && product.images.length > 1) {
            const interval = setInterval(() => {
                if (!isManualChange) {
                    setIsFadingOut(true);
                    setTimeout(() => {
                        setSelectedImageIndex((prevIndex) =>
                            prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
                        );
                        setIsFadingOut(false);
                    }, 300);
                } else {
                    setIsManualChange(false);
                }
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [product, isManualChange]);

    const handleManualImageChange = (index: number) => {
        setIsFadingOut(true);
        setTimeout(() => {
            setSelectedImageIndex(index);
            setIsManualChange(true);
            setIsFadingOut(false);
        }, 300);
    };

    if (!product) {
        return (
            <div className="min-h-[calc(100vh-130px)] flex items-center justify-center bg-gray-900 text-white">
                <div className="w-24 h-24 border-8 border-t-sky-800 border-slate-400 rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-130px)] bg-gray-900 text-white flex justify-center items-center p-8">
            <div className="flex w-4/6 bg-gradient-to-r from-slate-800 to-gray-800 rounded-lg shadow-lg p-4">
                <div className="w-full flex flex-col items-center">
                    <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Tektur, serif' }}>
                        {product.title}
                    </h1>

                    <div className="relative w-full flex justify-center mb-4">
                        <img
                            src={product.images[selectedImageIndex]}
                            alt={`${product.title} - Image ${selectedImageIndex + 1}`}
                            className={`rounded-lg max-h-[calc(100vh/2.4)] shadow-lg object-cover transition-opacity duration-200 ease-in-out ${
                                isFadingOut ? 'opacity-0' : 'opacity-100'
                            }`}
                        />
                    </div>

                    <div className="flex gap-2 mb-4">
                        {product.images.map((image, index) => (
                            <img
                                key={index}
                                src={image}
                                alt={`${product.title} ${index + 1}`}
                                className={`h-16 object-cover rounded border-2 transition ${
                                    selectedImageIndex === index ? 'border-sky-700' : 'border-transparent hover:border-sky-500 hover:shadow-[0_0_10px_2px_rgba(56,189,248,0.5)]'
                                }`}
                                onClick={() => handleManualImageChange(index)}
                            />
                        ))}
                    </div>

                    <div className="bg-gradient-to-r from-slate-900 to-gray-900 p-4 rounded-lg shadow-lg text-center mb-4">
                        <p className="text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>{product.description}</p>
                    </div>

                    <div className="flex gap-4">
                        <a
                            href={product.buy_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex  gap-2 bg-[#F15F2C] text-white font-bold px-8 py-3 rounded-lg shadow-lg hover:shadow-[0_0_10px_2px_rgba(241,95,44,0.5)] transition"
                            style={{ fontFamily: 'Montserrat, sans-serif' }} >
                            <SiBoosty size={22} />
                            Buy on Boosty.to
                        </a>
                    </div>                    
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;